import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

const CATEGORY_OPTIONS=[
  ["PROFILE","학생 기본정보"],
  ["CAREER_SUMMARY","진로목표·지원계획 요약"],
  ["ASSESSMENT_SUMMARY","검사 실시 요약"],
  ["ACTIVITY_SUMMARY","수업·활동 참여 요약"],
  ["EVIDENCE_SUMMARY","검증된 결과물 요약"],
  ["CREDENTIAL_SUMMARY","디지털배지 요약"]
] as const;

const RECIPIENT_OPTIONS=[
  ["GUARDIAN","보호자"],
  ["SCHOOL","학교"],
  ["EDUCATION_OFFICE","교육청·교육지원청"],
  ["EXTERNAL_INSTITUTION","외부기관"],
  ["OTHER","기타"]
] as const;

type LearnerRow={
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  guardianCount:number;
  pendingConsentCount:number;
  activeConsentCount:number;
  activeShareCount:number;
  lastConsentAt?:string|null;
};

type Purpose={
  purposeDefinitionId:string;
  purposeKey:string;
  purposeVersion:number;
  status:string;
  title:string;
  description:string;
  allowedCategories:string[];
  allowedRecipientTypes:string[];
  defaultValidDays:number;
  publishedAt:string;
  retiredAt?:string|null;
};

type RecipientOrg={
  organizationId:string;
  name:string;
  organizationType:string;
};

type Guardian={
  guardianId:string;
  displayName:string;
  relationshipType:string;
  maskedEmail:string;
  verificationStatus:string;
  status:string;
  startedAt:string;
  endedAt?:string|null;
};

type Consent={
  consentRequestId:string;
  guardianId:string;
  guardianName:string;
  guardianEmail:string;
  purposeDefinitionId:string;
  purpose:{title:string;description:string;purposeKey:string;purposeVersion:number};
  recipientType:string;
  recipientOrganizationId?:string|null;
  recipientName:string;
  dataCategories:string[];
  status:string;
  effectiveStatus:string;
  requestExpiresAt:string;
  requestedValidDays:number;
  consentValidUntil?:string|null;
  decisionSignerName?:string|null;
  decidedAt?:string|null;
  withdrawnAt?:string|null;
  createdAt:string;
  eventCount:number;
};

type Share={
  shareId:string;
  consentRequestId:string;
  purpose:{title:string;purposeKey:string;purposeVersion:number};
  recipient:{type:string;organizationId?:string|null;name:string};
  dataCategories:string[];
  projectionVersion:string;
  snapshotHash:string;
  status:string;
  effectiveStatus:string;
  expiresAt:string;
  createdAt:string;
  revokedAt?:string|null;
  revokeReason?:string|null;
  accessCount:number;
  lastAccessedAt?:string|null;
};

type SelectedLearner={
  learnerId:string;
  displayName:string;
  gradeLevel?:string|null;
  classLabel?:string|null;
  guardians:Guardian[];
  consents:Consent[];
  shares:Share[];
};

type Workspace={
  context:{canManagePurposes:boolean;schoolId:string};
  counts:{
    accessibleLearners:number;
    learnersWithoutGuardian:number;
    pendingConsent:number;
    activeConsent:number;
    activeShares:number;
    expiringShares7d:number;
  };
  learners:LearnerRow[];
  purposes:Purpose[];
  recipientOrganizations:RecipientOrg[];
  selectedLearner?:SelectedLearner|null;
};

function fmt(value?:string|null){
  if(!value)return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"
  });
}

function statusLabel(value:string){
  const map:Record<string,string>={
    PENDING:"동의 대기",GRANTED:"동의",DENIED:"미동의",WITHDRAWN:"철회",
    EXPIRED:"만료",CANCELLED:"취소",ACTIVE:"활성",REVOKED:"회수",
    PUBLISHED:"게시",RETIRED:"종료"
  };
  return map[value] || value;
}

function categoryLabel(value:string){
  return CATEGORY_OPTIONS.find(([key])=>key===value)?.[1] || value;
}

function recipientLabel(value:string){
  return RECIPIENT_OPTIONS.find(([key])=>key===value)?.[1] || value;
}

function navigate(path:string){
  window.history.pushState({},"",path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

async function copyText(text:string){
  await navigator.clipboard.writeText(text);
}

export function CenterPrivacyPage({
  client,schoolId
}:{
  client:PlatformClient;
  schoolId:string;
}){
  const initialStudent=new URLSearchParams(window.location.search).get("student") || "";
  const [data,setData]=useState<Workspace|null>(null);
  const [selectedLearnerId,setSelectedLearnerId]=useState(initialStudent);
  const [query,setQuery]=useState("");
  const [refresh,setRefresh]=useState(0);
  const [busy,setBusy]=useState("");
  const [error,setError]=useState("");
  const [message,setMessage]=useState("");
  const [issuedLink,setIssuedLink]=useState<{kind:string;url:string}|null>(null);

  const [guardianName,setGuardianName]=useState("");
  const [guardianRelation,setGuardianRelation]=useState("PARENT");
  const [guardianEmail,setGuardianEmail]=useState("");

  const [purposeId,setPurposeId]=useState("");
  const [guardianId,setGuardianId]=useState("");
  const [recipientType,setRecipientType]=useState("EXTERNAL_INSTITUTION");
  const [recipientOrgId,setRecipientOrgId]=useState("");
  const [recipientName,setRecipientName]=useState("");
  const [consentCategories,setConsentCategories]=useState<string[]>([]);
  const [consentDays,setConsentDays]=useState("30");

  const [shareConsentId,setShareConsentId]=useState("");
  const [shareCategories,setShareCategories]=useState<string[]>([]);
  const [shareHours,setShareHours]=useState("168");

  const [purposeKey,setPurposeKey]=useState("EXTERNAL_CAREER_SUPPORT");
  const [purposeTitle,setPurposeTitle]=useState("");
  const [purposeDescription,setPurposeDescription]=useState("");
  const [purposeCategories,setPurposeCategories]=useState<string[]>(["PROFILE","ACTIVITY_SUMMARY","EVIDENCE_SUMMARY"]);
  const [purposeRecipients,setPurposeRecipients]=useState<string[]>(["EXTERNAL_INSTITUTION"]);
  const [purposeDays,setPurposeDays]=useState("90");

  useEffect(()=>{
    let active=true;
    setError("");
    client.rpc("center_privacy_workspace",{
      p_school_id:schoolId,
      p_learner_id:selectedLearnerId || null
    }).then(({data,error})=>{
      if(!active)return;
      if(error){setError(error.message);return;}
      const next=data as Workspace;
      setData(next);

      const selected=next.selectedLearner;
      if(!selectedLearnerId && next.learners[0]){
        setSelectedLearnerId(next.learners[0].learnerId);
        return;
      }

      if(selected){
        const activeGuardians=selected.guardians.filter((item)=>item.status==="ACTIVE");
        setGuardianId((current)=>
          activeGuardians.some((item)=>item.guardianId===current)
            ? current
            : activeGuardians[0]?.guardianId || ""
        );
        const granted=selected.consents.filter((item)=>item.effectiveStatus==="GRANTED");
        setShareConsentId((current)=>
          granted.some((item)=>item.consentRequestId===current)
            ? current
            : granted[0]?.consentRequestId || ""
        );
      }

      const published=next.purposes.filter((item)=>item.status==="PUBLISHED");
      setPurposeId((current)=>
        published.some((item)=>item.purposeDefinitionId===current)
          ? current
          : published[0]?.purposeDefinitionId || ""
      );
      setRecipientOrgId((current)=>
        next.recipientOrganizations.some((item)=>item.organizationId===current)
          ? current
          : next.recipientOrganizations[0]?.organizationId || ""
      );
    });
    return()=>{active=false;};
  },[client,schoolId,selectedLearnerId,refresh]);

  const selectedPurpose=useMemo(
    ()=>data?.purposes.find((item)=>item.purposeDefinitionId===purposeId) || null,
    [data,purposeId]
  );

  const selectedConsent=useMemo(
    ()=>data?.selectedLearner?.consents.find((item)=>item.consentRequestId===shareConsentId) || null,
    [data,shareConsentId]
  );

  useEffect(()=>{
    if(!selectedPurpose)return;
    setConsentCategories(selectedPurpose.allowedCategories);
    setRecipientType((current)=>
      selectedPurpose.allowedRecipientTypes.includes(current)
        ? current
        : selectedPurpose.allowedRecipientTypes[0] || "OTHER"
    );
    setConsentDays(String(selectedPurpose.defaultValidDays));
  },[selectedPurpose?.purposeDefinitionId]);

  useEffect(()=>{
    if(!selectedConsent)return;
    setShareCategories(selectedConsent.dataCategories);
  },[selectedConsent?.consentRequestId]);

  useEffect(()=>{
    if(!data)return;
    const org=data.recipientOrganizations.find((item)=>item.organizationId===recipientOrgId);
    if(org && ["SCHOOL","EDUCATION_OFFICE","EXTERNAL_INSTITUTION"].includes(recipientType)){
      setRecipientName(org.name);
    }else if(recipientType==="GUARDIAN"){
      const guardian=data.selectedLearner?.guardians.find((item)=>item.guardianId===guardianId);
      setRecipientName(guardian?.displayName || "");
    }
  },[recipientType,recipientOrgId,guardianId,data?.selectedLearner?.learnerId]);

  async function run(key:string,action:()=>PromiseLike<{data?:unknown;error?:{message:string}|null}>,success?:string){
    setBusy(key);setError("");setMessage("");
    try{
      const result=await action();
      if(result.error)throw new Error(result.error.message);
      if(success)setMessage(success);
      setRefresh((value)=>value+1);
      return result.data;
    }catch(err){
      setError(err instanceof Error?err.message:"처리 중 오류가 발생했습니다.");
      return null;
    }finally{setBusy("");}
  }

  const visibleLearners=useMemo(()=>{
    if(!data)return [];
    const q=query.trim().toLowerCase();
    return data.learners.filter((item)=>{
      if(!q)return true;
      return [
        item.displayName,
        item.gradeLevel?item.gradeLevel+"학년":"",
        item.classLabel?item.classLabel+"반":""
      ].join(" ").toLowerCase().includes(q);
    });
  },[data,query]);

  function toggle(list:string[],value:string,setter:(next:string[])=>void){
    setter(list.includes(value)?list.filter((item)=>item!==value):[...list,value]);
  }

  async function createGuardian(){
    if(!selectedLearnerId || !guardianName.trim() || !guardianEmail.trim())return;
    const id=await run("guardian",()=>client.rpc("center_upsert_learner_guardian",{
      p_school_id:schoolId,p_learner_id:selectedLearnerId,
      p_display_name:guardianName.trim(),p_relationship_type:guardianRelation,
      p_email:guardianEmail.trim()
    }),"보호자 연락처를 등록했습니다.");
    if(id){setGuardianName("");setGuardianEmail("");}
  }

  async function endGuardian(id:string){
    await run("guardian-end",()=>client.rpc("center_end_learner_guardian",{
      p_school_id:schoolId,p_guardian_id:id,p_note:"동의·공유 화면에서 종료"
    }),"보호자 연락처를 종료했습니다.");
  }

  async function createConsent(){
    if(!selectedLearnerId || !guardianId || !purposeId || !recipientName.trim() || !consentCategories.length)return;
    const orgRequired=["SCHOOL","EDUCATION_OFFICE","EXTERNAL_INSTITUTION"].includes(recipientType);
    const result=await run("consent",()=>client.rpc("center_create_guardian_consent_request",{
      p_school_id:schoolId,p_learner_id:selectedLearnerId,p_guardian_id:guardianId,
      p_purpose_definition_id:purposeId,p_recipient_type:recipientType,
      p_recipient_organization_id:orgRequired?recipientOrgId:null,
      p_recipient_name:recipientName.trim(),p_data_categories:consentCategories,
      p_requested_valid_days:Number(consentDays),p_link_valid_minutes:20160
    }),"보호자 동의 링크를 발급했습니다.");
    if(result){
      const value=result as {consentUrl:string};
      setIssuedLink({kind:"보호자 동의",url:value.consentUrl});
    }
  }

  async function cancelConsent(id:string){
    await run("consent-cancel",()=>client.rpc("center_cancel_guardian_consent_request",{
      p_school_id:schoolId,p_consent_request_id:id,p_note:"학교에서 요청 취소"
    }),"동의 요청을 취소했습니다.");
  }

  async function createShare(){
    if(!shareConsentId || !shareCategories.length)return;
    const result=await run("share",()=>client.rpc("center_create_data_share_from_consent",{
      p_school_id:schoolId,p_consent_request_id:shareConsentId,
      p_data_categories:shareCategories,p_valid_hours:Number(shareHours)
    }),"최소정보 공유 링크를 발급했습니다.");
    if(result){
      const value=result as {shareUrl:string};
      setIssuedLink({kind:"데이터 공유",url:value.shareUrl});
    }
  }

  async function revokeShare(id:string){
    await run("share-revoke",()=>client.rpc("center_revoke_data_share",{
      p_school_id:schoolId,p_share_id:id,p_reason:"학교 담당자가 공유 종료"
    }),"데이터 공유를 종료했습니다.");
  }

  async function publishPurpose(){
    if(!purposeKey.trim() || !purposeTitle.trim() || !purposeDescription.trim() || !purposeCategories.length || !purposeRecipients.length)return;
    const result=await run("purpose",()=>client.rpc("center_create_consent_purpose_definition",{
      p_school_id:schoolId,p_purpose_key:purposeKey.trim(),
      p_title:purposeTitle.trim(),p_description:purposeDescription.trim(),
      p_allowed_categories:purposeCategories,p_allowed_recipient_types:purposeRecipients,
      p_default_valid_days:Number(purposeDays)
    }),"공유목적 새 버전을 게시했습니다.");
    if(result){setPurposeTitle("");setPurposeDescription("");}
  }

  async function retirePurpose(id:string){
    await run("purpose-retire",()=>client.rpc("center_retire_consent_purpose_definition",{
      p_school_id:schoolId,p_definition_id:id
    }),"공유목적을 종료했습니다.");
  }

  if(!data)return <section>{error?<p className="error">{error}</p>:<p className="muted">동의·공유 현황을 불러오는 중…</p>}</section>;

  const selected=data.selectedLearner;
  const publishedPurposes=data.purposes.filter((item)=>item.status==="PUBLISHED");
  const activeGuardians=selected?.guardians.filter((item)=>item.status==="ACTIVE") || [];
  const grantedConsents=selected?.consents.filter((item)=>item.effectiveStatus==="GRANTED") || [];

  return (
    <section className="erp12-privacy-page">
      <header className="erp12-head">
        <div>
          <p className="eyebrow">Consent & Data Sharing Governance</p>
          <h2>동의·공유</h2>
          <p>무엇을, 왜, 누구에게, 언제까지 공유하는지 먼저 확인합니다.</p>
        </div>
        <div className="erp12-privacy-badge">
          <strong>최소정보 원칙</strong>
          <span>검사 원점수 · 상담노트 · 파일은 공유하지 않음</span>
        </div>
      </header>

      {error && <p className="error">{error}</p>}
      {message && <p className="teacher-success">{message}</p>}

      {issuedLink && (
        <div className="erp12-issued-link">
          <div>
            <strong>{issuedLink.kind} 링크가 발급되었습니다.</strong>
            <span>보안을 위해 이 원문 링크는 지금만 다시 확인할 수 있습니다.</span>
            <code>{issuedLink.url}</code>
          </div>
          <button className="primary compact-button" onClick={async()=>{await copyText(issuedLink.url);setMessage("링크를 복사했습니다.");}}>링크 복사</button>
        </div>
      )}

      <div className="erp12-metrics">
        <Metric label="접근 가능 학생" value={data.counts.accessibleLearners}/>
        <Metric label="보호자 미등록" value={data.counts.learnersWithoutGuardian}/>
        <Metric label="동의 대기" value={data.counts.pendingConsent}/>
        <Metric label="유효 동의" value={data.counts.activeConsent}/>
        <Metric label="활성 공유" value={data.counts.activeShares}/>
        <Metric label="7일 내 만료" value={data.counts.expiringShares7d}/>
      </div>

      <div className="erp12-layout">
        <aside className="erp12-learner-panel">
          <div className="panel-title"><strong>학생</strong><span>{visibleLearners.length}/{data.learners.length}명</span></div>
          <input className="teacher-search" value={query} onChange={(e)=>setQuery(e.target.value)} placeholder="학생·학년·반 검색"/>
          <div className="erp12-learner-list">
            {visibleLearners.map((item)=>(
              <button key={item.learnerId} className={selectedLearnerId===item.learnerId?"active":""} onClick={()=>setSelectedLearnerId(item.learnerId)}>
                <span><strong>{item.displayName}</strong><small>{item.gradeLevel?item.gradeLevel+"학년":"학년 미정"}{item.classLabel?" · "+item.classLabel+"반":""}</small></span>
                <span className="erp12-learner-counts"><b>보호자 {item.guardianCount}</b><b>동의 {item.activeConsentCount}</b><b>공유 {item.activeShareCount}</b></span>
              </button>
            ))}
          </div>
        </aside>

        <main className="erp12-main">
          {!selected ? (
            <div className="empty-inline large"><strong>학생을 선택하세요.</strong><span>보호자, 동의, 공유 이력을 확인할 수 있습니다.</span></div>
          ) : (
            <>
              <header className="erp12-student-head">
                <div><p className="eyebrow">학생별 동의·공유</p><h3>{selected.displayName}</h3><p>{selected.gradeLevel?selected.gradeLevel+"학년":""}{selected.classLabel?" · "+selected.classLabel+"반":""}</p></div>
                <button className="secondary" onClick={()=>navigate("/learners?student="+encodeURIComponent(selected.learnerId))}>Student 360</button>
              </header>

              <div className="erp12-grid">
                <section className="erp12-card">
                  <div className="panel-title"><strong>보호자</strong><span>{activeGuardians.length}명 활성</span></div>
                  <div className="erp12-row-list">
                    {selected.guardians.map((item)=>(
                      <div key={item.guardianId} className={item.status==="ACTIVE"?"":"muted-row"}>
                        <span><strong>{item.displayName}</strong><small>{item.relationshipType} · {item.maskedEmail} · {item.verificationStatus}</small></span>
                        <b>{statusLabel(item.status)}</b>
                        {item.status==="ACTIVE" && <button className="text-button" disabled={busy!==""} onClick={()=>endGuardian(item.guardianId)}>종료</button>}
                      </div>
                    ))}
                  </div>
                  <div className="erp12-form-grid">
                    <label>관계<select value={guardianRelation} onChange={(e)=>setGuardianRelation(e.target.value)}><option value="PARENT">부모</option><option value="LEGAL_GUARDIAN">법정대리인</option><option value="CAREGIVER">보호·양육자</option><option value="OTHER">기타</option></select></label>
                    <label>보호자 이름<input value={guardianName} onChange={(e)=>setGuardianName(e.target.value)}/></label>
                    <label className="wide">이메일<input type="email" value={guardianEmail} onChange={(e)=>setGuardianEmail(e.target.value)} placeholder="guardian@example.com"/></label>
                  </div>
                  <button className="secondary" disabled={busy!=="" || !guardianName.trim() || !guardianEmail.trim()} onClick={createGuardian}>보호자 등록</button>
                </section>

                <section className="erp12-card">
                  <div className="panel-title"><strong>보호자 동의 요청</strong><span>목적·수신자·범위를 먼저 고정</span></div>
                  {!publishedPurposes.length ? <p className="case-empty">게시된 공유목적이 없습니다. 학교관리자가 먼저 공유목적을 게시해야 합니다.</p> : !activeGuardians.length ? <p className="case-empty">동의 요청 전에 보호자를 등록하세요.</p> : (
                    <>
                      <div className="erp12-form-grid">
                        <label>보호자<select value={guardianId} onChange={(e)=>setGuardianId(e.target.value)}>{activeGuardians.map((item)=><option key={item.guardianId} value={item.guardianId}>{item.displayName} · {item.maskedEmail}</option>)}</select></label>
                        <label>공유목적<select value={purposeId} onChange={(e)=>setPurposeId(e.target.value)}>{publishedPurposes.map((item)=><option key={item.purposeDefinitionId} value={item.purposeDefinitionId}>{item.title} · v{item.purposeVersion}</option>)}</select></label>
                        <label>수신자 유형<select value={recipientType} onChange={(e)=>setRecipientType(e.target.value)}>{(selectedPurpose?.allowedRecipientTypes || []).map((item)=><option key={item} value={item}>{recipientLabel(item)}</option>)}</select></label>
                        {["SCHOOL","EDUCATION_OFFICE","EXTERNAL_INSTITUTION"].includes(recipientType) && <label>수신기관<select value={recipientOrgId} onChange={(e)=>setRecipientOrgId(e.target.value)}>{data.recipientOrganizations.map((item)=><option key={item.organizationId} value={item.organizationId}>{item.name} · {item.organizationType}</option>)}</select></label>}
                        <label className="wide">수신자 표시명<input value={recipientName} onChange={(e)=>setRecipientName(e.target.value)} placeholder="기관 또는 담당 주체"/></label>
                        <label>동의 유효일<input type="number" min="1" max={selectedPurpose?.defaultValidDays || 3650} value={consentDays} onChange={(e)=>setConsentDays(e.target.value)}/></label>
                      </div>
                      <div className="erp12-checks">
                        {(selectedPurpose?.allowedCategories || []).map((item)=><label key={item}><input type="checkbox" checked={consentCategories.includes(item)} onChange={()=>toggle(consentCategories,item,setConsentCategories)}/>{categoryLabel(item)}</label>)}
                      </div>
                      <button className="primary" disabled={busy!=="" || !guardianId || !purposeId || !recipientName.trim() || !consentCategories.length} onClick={createConsent}>보호자 동의 링크 발급</button>
                    </>
                  )}

                  <div className="erp12-consent-list">
                    {selected.consents.map((item)=>(
                      <article key={item.consentRequestId}>
                        <header><div><strong>{item.purpose?.title || "공유 동의"}</strong><span>{item.guardianName} · {item.recipientName}</span></div><b>{statusLabel(item.effectiveStatus)}</b></header>
                        <p>{item.dataCategories.map(categoryLabel).join(" · ")}</p>
                        <small>요청 {fmt(item.createdAt)} · 동의유효 {fmt(item.consentValidUntil)} · 이벤트 {item.eventCount}</small>
                        {item.effectiveStatus==="PENDING" && <button className="text-button" disabled={busy!==""} onClick={()=>cancelConsent(item.consentRequestId)}>요청 취소</button>}
                      </article>
                    ))}
                    {!selected.consents.length && <p className="case-empty">동의 요청 이력이 없습니다.</p>}
                  </div>
                </section>

                <section className="erp12-card">
                  <div className="panel-title"><strong>최소정보 공유</strong><span>유효 동의 범위 내에서만 발급</span></div>
                  {!grantedConsents.length ? <p className="case-empty">현재 유효한 보호자 동의가 없습니다.</p> : (
                    <>
                      <div className="erp12-form-grid">
                        <label className="wide">동의 선택<select value={shareConsentId} onChange={(e)=>setShareConsentId(e.target.value)}>{grantedConsents.map((item)=><option key={item.consentRequestId} value={item.consentRequestId}>{item.purpose?.title || "공유 동의"} · {item.recipientName} · {fmt(item.consentValidUntil)}</option>)}</select></label>
                        <label>공유시간<input type="number" min="1" max="2160" value={shareHours} onChange={(e)=>setShareHours(e.target.value)}/></label>
                      </div>
                      <div className="erp12-checks">
                        {(selectedConsent?.dataCategories || []).map((item)=><label key={item}><input type="checkbox" checked={shareCategories.includes(item)} onChange={()=>toggle(shareCategories,item,setShareCategories)}/>{categoryLabel(item)}</label>)}
                      </div>
                      <div className="erp12-privacy-note"><strong>공유하지 않는 정보</strong><span>검사 원점수·백분위·해석 원문, 상담 세부노트, Evidence 파일, Case 공유노트</span></div>
                      <button className="primary" disabled={busy!=="" || !shareConsentId || !shareCategories.length} onClick={createShare}>최소정보 Share 발급</button>
                    </>
                  )}
                  <div className="erp12-share-list">
                    {selected.shares.map((item)=>(
                      <article key={item.shareId}>
                        <header><div><strong>{item.purpose?.title || "데이터 공유"}</strong><span>{item.recipient?.name || "-"}</span></div><b>{statusLabel(item.effectiveStatus)}</b></header>
                        <p>{item.dataCategories.map(categoryLabel).join(" · ")}</p>
                        <small>만료 {fmt(item.expiresAt)} · 열람 {item.accessCount}회 · 최근 {fmt(item.lastAccessedAt)}</small>
                        <code>{item.snapshotHash.slice(0,18)}… · {item.projectionVersion}</code>
                        {item.effectiveStatus==="ACTIVE" && <button className="text-button danger" disabled={busy!==""} onClick={()=>revokeShare(item.shareId)}>공유 종료</button>}
                      </article>
                    ))}
                    {!selected.shares.length && <p className="case-empty">공유 발급 이력이 없습니다.</p>}
                  </div>
                </section>
              </div>
            </>
          )}
        </main>
      </div>

      {data.context.canManagePurposes && (
        <details className="erp12-purpose-governance">
          <summary>학교 공유목적 관리</summary>
          <div className="erp12-purpose-body">
            <section>
              <h3>새 목적 버전 게시</h3>
              <p className="muted">법 조항을 코드에 고정하지 않고 학교가 승인한 목적·범위·유효기간을 버전으로 관리합니다.</p>
              <div className="erp12-form-grid">
                <label>목적키<input value={purposeKey} onChange={(e)=>setPurposeKey(e.target.value)}/></label>
                <label>기본 유효일<input type="number" min="1" max="3650" value={purposeDays} onChange={(e)=>setPurposeDays(e.target.value)}/></label>
                <label className="wide">제목<input value={purposeTitle} onChange={(e)=>setPurposeTitle(e.target.value)}/></label>
                <label className="wide">설명<textarea rows={3} value={purposeDescription} onChange={(e)=>setPurposeDescription(e.target.value)}/></label>
              </div>
              <strong>허용 정보</strong>
              <div className="erp12-checks">{CATEGORY_OPTIONS.map(([key,label])=><label key={key}><input type="checkbox" checked={purposeCategories.includes(key)} onChange={()=>toggle(purposeCategories,key,setPurposeCategories)}/>{label}</label>)}</div>
              <strong>허용 수신자</strong>
              <div className="erp12-checks">{RECIPIENT_OPTIONS.map(([key,label])=><label key={key}><input type="checkbox" checked={purposeRecipients.includes(key)} onChange={()=>toggle(purposeRecipients,key,setPurposeRecipients)}/>{label}</label>)}</div>
              <button className="primary" disabled={busy!=="" || !purposeTitle.trim() || !purposeDescription.trim() || !purposeCategories.length || !purposeRecipients.length} onClick={publishPurpose}>새 버전 게시</button>
            </section>
            <section className="erp12-purpose-list">
              {data.purposes.map((item)=>(
                <article key={item.purposeDefinitionId}>
                  <header><div><strong>{item.title}</strong><span>{item.purposeKey} · v{item.purposeVersion}</span></div><b>{statusLabel(item.status)}</b></header>
                  <p>{item.description}</p>
                  <small>{item.allowedCategories.map(categoryLabel).join(" · ")} / {item.allowedRecipientTypes.map(recipientLabel).join(" · ")}</small>
                  {item.status==="PUBLISHED" && <button className="text-button" disabled={busy!==""} onClick={()=>retirePurpose(item.purposeDefinitionId)}>목적 종료</button>}
                </article>
              ))}
            </section>
          </div>
        </details>
      )}
    </section>
  );
}

function Metric({label,value}:{label:string;value:number}){
  return <div className="erp12-metric"><span>{label}</span><strong>{value}</strong></div>;
}
